import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    Typography,
    Box,
} from '@mui/material';
import { cryptoProService } from '../../services/cryptoProService';
import type { Certificate } from '../../models/digitalSignatures/cryptoProTypes';
import { DateUtils } from '../../helpers/dateUtils';
import Spinner from './Spinner';
import ErrorAlert from './ErrorAlert';

interface CertificateSelectionDialogProps {
    open: boolean;
    onClose: () => void;
    onSelect: (certificate: Certificate) => void;
}

export const CertificateSelectionDialog: React.FC<CertificateSelectionDialogProps> = ({
    open,
    onClose,
    onSelect,
}) => {
    const [certificates, setCertificates] = useState<Certificate[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            loadCertificates();
        }
    }, [open]);

    const loadCertificates = async () => {
        setLoading(true);
        setError(null);
        try {
            const certs = await cryptoProService.enumerateCertificates();
            setCertificates(certs);
            if (certs.length === 0) {
                setError('Действующие сертификаты с закрытым ключом не найдены. Убедитесь, что ключ подключен и сертификат не просрочен.');
            }
        } catch (error: any) {
            console.error('Ошибка загрузки сертификатов:', error);
            setError(cryptoProService.normalizeError(error));
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (certificate: Certificate) => {
        onSelect(certificate);
        onClose();
    };

    const formatDate = (date: Date): string => {
        return DateUtils.formatDate(date.toISOString());
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Выбор сертификата для подписания</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box position="relative" minHeight={200}>
                        <Spinner />
                    </Box>
                ) : error ? (
                    <ErrorAlert
                        message={error}
                        onClose={() => setError(null)}
                        autoCloseDuration={0}
                    />
                ) : certificates.length === 0 ? (
                    <Typography>Сертификаты не найдены</Typography>
                ) : (
                    <List>
                        {certificates.map((cert) => (
                            <ListItem
                                key={cert.thumbprint}
                                button
                                onClick={() => handleSelect(cert)}
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    mb: 1,
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                    },
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {cert.subject}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography variant="body2" color="text.secondary">
                                            Срок действия: {formatDate(cert.validFrom)} - {formatDate(cert.validTo)}
                                            {cert.serialNumber && ` | Серийный номер: ${cert.serialNumber}`}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Отмена</Button>
                {!loading && certificates.length > 0 && (
                    <Button onClick={loadCertificates} variant="outlined">
                        Обновить список
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

